import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, ArrowLeft, ArrowRight, MapPin, ChevronRight,
  Building2, Loader2, X, SlidersHorizontal,
} from "lucide-react";
import { apiClient } from "../lib/api";
import TeroumaLogo from "../components/ui/TeroumaLogo";

/* ─── Sorts ────────────────────────────────────────────────────────────── */
const SORTS = [
  { value: "alpha",    label: "A → Z" },
  { value: "alpha_z",  label: "Z → A" },
  { value: "city",     label: "Par ville" },
  { value: "oldest",   label: "Plus anciennes" },
];

/* ─── Card ─────────────────────────────────────────────────────────────── */
// Independent card mirroring the landing OrgCard, slightly richer for the
// dedicated directory page (shows founding year + tags + truncated description).
function OrgCardFull({ org }) {
  const initials = (org.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <Link
      to={`/organisations/${org.id}`}
      className="group bg-white border border-stone-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 flex flex-col no-underline"
    >
      {/* Photo / banner */}
      {org.org_photo_base64 ? (
        <div className="h-44 w-full overflow-hidden shrink-0 bg-stone-100">
          <img
            src={`data:image/jpeg;base64,${org.org_photo_base64}`}
            alt={org.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            style={{ objectPosition: "center 55%" }}
          />
        </div>
      ) : (
        <div
          className="h-1.5 w-full shrink-0"
          style={{ background: "linear-gradient(90deg, #0D1B33, #B8962E)" }}
        />
      )}

      <div className="p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start gap-3">
          <div
            className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0 text-sm font-bold text-white"
            style={{ background: "linear-gradient(135deg, #0D1B33 0%, #1B2A4A 100%)" }}
          >
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-stone-900 leading-tight truncate">{org.name}</p>
            <div className="flex items-center gap-1 mt-0.5">
              <MapPin className="h-3 w-3 text-stone-400 shrink-0" />
              <span className="text-xs text-stone-500 truncate">{org.city || "—"}</span>
              {org.founding_year && (
                <span className="text-xs text-stone-400 ml-1">· {org.founding_year}</span>
              )}
            </div>
          </div>
        </div>

        {org.tags && org.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {org.tags.slice(0, 4).map((tag) => (
              <span
                key={tag}
                className="text-[10px] px-2 py-0.5 rounded-full"
                style={{ background: "rgba(13,27,51,0.06)", color: "#0D1B33" }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {org.public_description && (
          <p className="text-xs text-stone-500 line-clamp-2">{org.public_description}</p>
        )}

        <div className="mt-auto pt-1 flex items-center justify-between">
          <span className="text-xs font-medium" style={{ color: "#B8962E" }}>Voir la fiche</span>
          <ChevronRight className="h-3.5 w-3.5" style={{ color: "#B8962E" }} />
        </div>
      </div>
    </Link>
  );
}

/* ─── Page ─────────────────────────────────────────────────────────────── */
export default function AllOrganizationsPage() {
  const [orgs, setOrgs] = useState(null);   // null while loading, [] when loaded
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState(null); // null = all
  const [sortBy, setSortBy] = useState("alpha");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    apiClient
      .get("/organizations/public/directory")
      .then((r) => setOrgs(r.data?.organizations || []))
      .catch(() => {
        setError(true);
        setOrgs([]);
      });
  }, []);

  // All unique tags across orgs, sorted by frequency desc.
  const allTags = useMemo(() => {
    if (!orgs || orgs.length === 0) return [];
    const counts = new Map();
    for (const o of orgs) {
      for (const t of o.tags || []) {
        counts.set(t, (counts.get(t) || 0) + 1);
      }
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([tag, n]) => ({ tag, n }));
  }, [orgs]);

  // Derived filtered + sorted list.
  const filtered = useMemo(() => {
    if (!orgs) return [];
    let list = [...orgs];

    if (tagFilter) {
      list = list.filter((o) => (o.tags || []).includes(tagFilter));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (o) =>
          (o.name || "").toLowerCase().includes(q) ||
          (o.city || "").toLowerCase().includes(q) ||
          (o.address || "").toLowerCase().includes(q) ||
          (o.tags || []).some((t) => t.toLowerCase().includes(q)) ||
          (o.public_description || "").toLowerCase().includes(q),
      );
    }

    const byName = (a, b) => (a.name || "").localeCompare(b.name || "", "fr");
    if (sortBy === "alpha") list.sort(byName);
    else if (sortBy === "alpha_z") list.sort((a, b) => byName(b, a));
    else if (sortBy === "city") list.sort((a, b) => (a.city || "").localeCompare(b.city || "", "fr") || byName(a, b));
    else if (sortBy === "oldest")
      list.sort(
        (a, b) =>
          (a.founding_year || Number.MAX_SAFE_INTEGER) - (b.founding_year || Number.MAX_SAFE_INTEGER),
      );
    return list;
  }, [orgs, search, tagFilter, sortBy]);

  const activeFilters = (tagFilter ? 1 : 0) + (search.trim() ? 1 : 0);
  const loading = orgs === null;

  return (
    <div className="min-h-screen" style={{ background: "#F4F3EF", fontFamily: "'Outfit', sans-serif" }}>
      {/* ── Navbar ─────────────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-stone-200/60">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <TeroumaLogo textColor="#0D1B33" />
          </Link>
          <Link
            to="/"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm text-stone-600 hover:text-stone-900 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Accueil
          </Link>
        </div>
      </nav>

      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <header className="px-6 pt-14 pb-10">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: "#B8962E" }}>
            Annuaire complet
          </p>
          <h1
            className="text-4xl sm:text-5xl font-light text-stone-900 tracking-tight"
            style={{ fontFamily: "'Space Grotesk', sans-serif", lineHeight: 1.1 }}
          >
            Toutes les organisations
          </h1>
          <p className="text-stone-500 mt-3 text-base max-w-xl mx-auto">
            Synagogues, communautés et associations juives partenaires de Terouma à travers la France.
          </p>
        </div>
      </header>

      {/* ── Filters bar ───────────────────────────────────────────────── */}
      <div className="px-6 mb-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl border border-stone-200 p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher par nom, ville, tag…"
                className="w-full pl-9 pr-9 py-2.5 rounded-xl bg-stone-50 border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  aria-label="Effacer la recherche"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-700"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2.5 rounded-xl bg-stone-50 border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10"
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>

            {/* Toggle filters (tags) */}
            {allTags.length > 0 && (
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                className="inline-flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm transition-colors border"
                style={{
                  background: tagFilter ? "rgba(184,150,46,0.10)" : "white",
                  borderColor: tagFilter ? "rgba(184,150,46,0.45)" : "#E5E7EB",
                  color: tagFilter ? "#8A6F22" : "#374151",
                }}
              >
                <SlidersHorizontal className="h-4 w-4" />
                {tagFilter ? `Tag: ${tagFilter}` : "Filtres"}
                {activeFilters > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-900 text-white">
                    {activeFilters}
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Tag chips */}
          {showFilters && allTags.length > 0 && (
            <div className="mt-3 bg-white rounded-2xl border border-stone-200 p-4">
              <p className="text-[11px] uppercase tracking-wider text-stone-500 mb-3">Filtrer par tag</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setTagFilter(null)}
                  className="text-xs px-3 py-1.5 rounded-full transition-colors border"
                  style={{
                    background: tagFilter === null ? "#0D1B33" : "white",
                    color: tagFilter === null ? "white" : "#0D1B33",
                    borderColor: tagFilter === null ? "#0D1B33" : "#E5E7EB",
                  }}
                >
                  Tous
                </button>
                {allTags.map(({ tag, n }) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setTagFilter(tagFilter === tag ? null : tag)}
                    className="text-xs px-3 py-1.5 rounded-full transition-colors border"
                    style={{
                      background: tagFilter === tag ? "#0D1B33" : "white",
                      color: tagFilter === tag ? "white" : "#0D1B33",
                      borderColor: tagFilter === tag ? "#0D1B33" : "#E5E7EB",
                    }}
                  >
                    {tag}
                    <span className="ml-1 opacity-60">({n})</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Result count */}
          <p className="text-xs text-stone-500 mt-3 px-1">
            {loading
              ? "Chargement…"
              : `${filtered.length} organisation${filtered.length > 1 ? "s" : ""}${
                  activeFilters > 0 ? ` · ${activeFilters} filtre${activeFilters > 1 ? "s" : ""} actif${activeFilters > 1 ? "s" : ""}` : ""
                }`}
          </p>
        </div>
      </div>

      {/* ── List ─────────────────────────────────────────────────────── */}
      <main className="px-6 pb-20">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-6 w-6 animate-spin" style={{ color: "#B8962E" }} />
            </div>
          ) : error ? (
            <div className="text-center py-24 text-stone-400">
              <Building2 className="h-12 w-12 mx-auto mb-3 text-stone-200" />
              <p>Impossible de charger l'annuaire pour l'instant.</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24 text-stone-400">
              <Building2 className="h-12 w-12 mx-auto mb-3 text-stone-200" />
              <p>Aucune organisation ne correspond à votre recherche.</p>
              {activeFilters > 0 && (
                <button
                  type="button"
                  onClick={() => { setSearch(""); setTagFilter(null); }}
                  className="inline-flex items-center gap-1.5 text-sm mt-4"
                  style={{ color: "#B8962E" }}
                >
                  Réinitialiser les filtres <ArrowRight className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map((org) => (
                <OrgCardFull key={org.id} org={org} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
